import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  exportPayments,
  getBillingReconciliation,
  getCollectionsTrend,
  getMpesaTransaction,
  getPayNowInfo,
  getPaymentMethodBreakdown,
  getPaymentsSummary,
  initiateStkPush,
  listPayments,
  listUnmatchedC2BTransactions,
  payQuickPay,
  peekQuickPay,
  recordPayment,
  resolveC2BTransaction,
  type ListPaymentsFilters,
  type PaymentsReportRange,
  type RecordPaymentInput,
  type StkPushTarget,
} from '@/lib/payments'

export function useRecordPayment() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: RecordPaymentInput) => recordPayment(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['invoices'] })
      qc.invalidateQueries({ queryKey: ['arrears'] })
    },
  })
}

export function useInitiateStkPush() {
  return useMutation({
    mutationFn: ({ phone, target }: { phone?: string; target?: StkPushTarget } = {}) =>
      initiateStkPush(phone, target),
  })
}

export function usePayNowInfo(utilityTypeId?: string) {
  return useQuery({
    queryKey: ['pay-now-info', utilityTypeId ?? null],
    queryFn: () => getPayNowInfo(utilityTypeId),
  })
}

export function useQuickPayPeek(token: string | null) {
  return useQuery({
    queryKey: ['quick-pay-peek', token],
    queryFn: () => peekQuickPay(token!),
    enabled: token !== null,
    retry: false,
  })
}

export function useQuickPay() {
  return useMutation({
    mutationFn: ({ token, phone }: { token: string; phone?: string }) => payQuickPay(token, phone),
  })
}

const POLL_TIMEOUT_MS = 60_000

/** Polls every 3s while pending, stops once resolved or after ~60s (the callback can still land later). */
export function useMpesaTransactionPoll(transactionId: string | null, startedAt: number | null) {
  const qc = useQueryClient()
  return useQuery({
    queryKey: ['mpesa-transaction', transactionId],
    queryFn: () => getMpesaTransaction(transactionId!),
    enabled: transactionId !== null,
    refetchInterval: (query) => {
      const status = query.state.data?.data.status
      if (status && status !== 'pending') {
        if (status === 'successful') {
          qc.invalidateQueries({ queryKey: ['invoices'] })
          qc.invalidateQueries({ queryKey: ['member-invoices'] })
        }
        return false
      }
      if (startedAt && Date.now() - startedAt > POLL_TIMEOUT_MS) return false
      return 3000
    },
  })
}

export function usePaymentsSummary(range: PaymentsReportRange, enabled = true) {
  return useQuery({
    queryKey: ['payments-summary', range],
    queryFn: () => getPaymentsSummary(range),
    enabled,
  })
}

export function useBillingReconciliation(range: PaymentsReportRange, enabled = true) {
  return useQuery({
    queryKey: ['billing-reconciliation', range],
    queryFn: () => getBillingReconciliation(range),
    enabled,
  })
}

export function useCollectionsTrend(range: PaymentsReportRange, enabled = true) {
  return useQuery({
    queryKey: ['collections-trend', range],
    queryFn: () => getCollectionsTrend(range),
    enabled,
  })
}

export function usePaymentMethodBreakdown(range: PaymentsReportRange, enabled = true) {
  return useQuery({
    queryKey: ['payment-method-breakdown', range],
    queryFn: () => getPaymentMethodBreakdown(range),
    enabled,
  })
}

/** The actual payment transactions within a range — same data the CSV export uses, rendered
 * on screen instead of downloaded. */
export function usePaymentsList(range: PaymentsReportRange, enabled = true) {
  return useQuery({
    queryKey: ['payments-list', range],
    queryFn: () => exportPayments(range),
    enabled,
  })
}

export function usePayments(filters: ListPaymentsFilters = {}) {
  return useQuery({
    queryKey: ['payments', filters],
    queryFn: () => listPayments(filters),
  })
}

export function useUnmatchedC2BTransactions() {
  return useQuery({
    queryKey: ['c2b-transactions'],
    queryFn: () => listUnmatchedC2BTransactions(),
  })
}

export function useResolveC2BTransaction() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      transactionId,
      memberId,
      target,
    }: {
      transactionId: string
      memberId: string
      target?: StkPushTarget
    }) => resolveC2BTransaction(transactionId, memberId, target),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['c2b-transactions'] })
      qc.invalidateQueries({ queryKey: ['invoices'] })
      qc.invalidateQueries({ queryKey: ['arrears'] })
    },
  })
}
